import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChooseListInput } from '@/shared/components/inputs/ChooseList.input';
import { WizardStepProps } from '../types/wizard.types';
import { ComponentReturnTypeMap } from '@momentum/types';

export interface GoalOption {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

interface WizardGoalsChoiceStepProps
  extends WizardStepProps<ComponentReturnTypeMap['GoalsChoice']> {
  goals: GoalOption[];
  columns?: 1 | 2 | 3;
}

export const WizardGoalsChoiceStep: React.FC<WizardGoalsChoiceStepProps> = ({
  value,
  onChange,
  onNext,
  onPrevious: _onPrevious,
  error,
  disabled,
  goals,
  columns = 2,
  required = false,
}) => {
  const { t } = useTranslation('wizard');
  const handleChange = (selectedGoalId: string) => {
    onChange(selectedGoalId);
    // Automatically proceed to next step when a goal is selected
    if (selectedGoalId && onNext) {
      setTimeout(() => {
        onNext(selectedGoalId);
      }, 100); // Small delay for better UX
    }
  };

  // Add default goal options if none provided
  const defaultGoals: GoalOption[] = [
    {
      id: 'no-effect',
      title: t('common.goalsChoice.noEffect.title'),
      fullWidth: true,
      icon: (
        <svg
          className="w-6 h-6 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728"
          />
        </svg>
      ),
    },
  ];

  const allGoals =
    goals.length > 0 ? [...goals, ...defaultGoals] : defaultGoals;

  return (
    <div className="w-full mx-auto">
      <ChooseListInput
        options={allGoals.map((goal) => ({ ...goal, description: '' }))}
        value={value}
        onChange={handleChange}
        variant="card"
        columns={columns}
        title=""
        description=""
        required={required}
        disabled={disabled}
        className="w-full"
        multiSelect={false}
      />

      {error && (
        <p className="mt-4 text-sm text-red-600 text-center">{error}</p>
      )}
    </div>
  );
};

/* 
MongoDB Documents that should use this component:
- All questions with type: "goals-choice" (14 documents)
- Examples: 
  - "Which one of your goals is affected by recipientPlaceholder meeting expectations?"
  - Questions asking users to select which personal or professional goal was impacted
  - Always includes "Does not affect my goals" as an option
*/
